import { resolveDirectionsServiceLabel } from "../../constants/directions-service.const";
import {
  REBUILD_TYPE_TRANSLATION_KEY_MAP,
  ROUTE_REBUILD_SOURCE_TRANSLATION_KEY_MAP,
} from "../../constants/rebuild-route.const";
import {
  DirectionsServiceEnum,
  IgnoreMLPathReasonEnum,
  RebuildRouteTypeEnum,
  RouteRebuildSourceEnum,
} from "../../enums";
import {
  IFormatRebuildRouteAppliedInfoParams,
  IFormatRebuildRouteLastUsedProviderLabelParams,
  IFormatRebuildRouteProviderLabelParams,
  IFormatRebuildRouteRerouteButtonLabelParams,
  IFormatRebuildRouteRerouteSettingsParams,
  IRebuildRouteStackedButtonCopy,
  IResolveInitialRebuildRouteProviderParams,
  IResolveRebuildRouteProviderParams,
  TDefaultActionRebuildRouteType,
  TRebuildRouteTranslateFn,
} from "./rebuild-route.models";

/** Initial provider in rebuild dropdown: last used engine, else system default. */
export const resolveInitialRebuildRouteProvider = ({
  lastUsedProvider,
  systemDefaultProvider,
}: IResolveInitialRebuildRouteProviderParams): DirectionsServiceEnum =>
  lastUsedProvider ?? systemDefaultProvider;

/** Effective provider for rebuild: dropdown selection, else last used, else system default. */
export const resolveRebuildRouteProvider = ({
  selectedProvider,
  lastUsedProvider,
  systemDefaultProvider,
}: IResolveRebuildRouteProviderParams): DirectionsServiceEnum =>
  selectedProvider ??
  resolveInitialRebuildRouteProvider({
    lastUsedProvider,
    systemDefaultProvider,
  });

export const formatRebuildRouteTooltip = ({
  baseLabel,
  lastUsedProvider,
  systemDefaultProvider,
}: {
  baseLabel: string;
  lastUsedProvider?: DirectionsServiceEnum | null;
  systemDefaultProvider: DirectionsServiceEnum;
}): string => {
  const provider = resolveInitialRebuildRouteProvider({
    lastUsedProvider,
    systemDefaultProvider,
  });
  const providerLabel = resolveDirectionsServiceLabel(provider);

  return providerLabel ? `${baseLabel} (${providerLabel})` : baseLabel;
};

export const isLastUsedRouteProviderDifferentFromSystem = ({
  lastUsedProvider,
  systemDefaultProvider,
}: IResolveInitialRebuildRouteProviderParams): boolean =>
  Boolean(lastUsedProvider) && lastUsedProvider !== systemDefaultProvider;

const resolveProviderLabel = (provider: DirectionsServiceEnum): string =>
  resolveDirectionsServiceLabel(provider) ?? provider;

export const formatRebuildRouteLastUsedProviderLabel = ({
  lastUsedProvider,
  t,
}: IFormatRebuildRouteLastUsedProviderLabelParams): string =>
  t("general.REBUILD_ROUTE_LAST_USED_PROVIDER", {
    provider: resolveProviderLabel(lastUsedProvider),
  });

export const formatRebuildRouteSystemDefaultLabel = ({
  provider,
  t,
}: IFormatRebuildRouteProviderLabelParams): string =>
  t("general.REBUILD_ROUTE_SYSTEM_DEFAULT", {
    provider: resolveProviderLabel(provider),
  });

export const formatRebuildRouteResetToProviderLabel = ({
  provider,
  t,
}: IFormatRebuildRouteProviderLabelParams): string =>
  t("general.REBUILD_ROUTE_RESET_TO_PROVIDER", {
    provider: resolveProviderLabel(provider),
  });

const formatRebuildRouteSourceLabel = (
  routeRebuildSource: RouteRebuildSourceEnum | null | undefined,
  t: TRebuildRouteTranslateFn,
): string | undefined => {
  if (!routeRebuildSource) {
    return undefined;
  }

  return t(ROUTE_REBUILD_SOURCE_TRANSLATION_KEY_MAP[routeRebuildSource]);
};

/** Section heading above the default rebuild plate. */
export const formatRebuildRouteDefaultActionHeading = ({
  hasProductionId = false,
  t,
}: {
  hasProductionId?: boolean;
  t: TRebuildRouteTranslateFn;
}): string =>
  hasProductionId
    ? t("general.REBUILD_SAVE_CACHE_WITH_PRODUCE")
    : t("general.REBUILD_SAVE_CACHE");

/** Default-action submit: rebuild this route and write system cache. */
export const formatRebuildRouteSaveCacheButtonCopy = ({
  lastUsedProvider,
  systemDefaultProvider,
  skipCachedPaths = false,
  t,
}: IResolveInitialRebuildRouteProviderParams & {
  skipCachedPaths?: boolean;
  t: TRebuildRouteTranslateFn;
}): IRebuildRouteStackedButtonCopy => {
  const provider = resolveInitialRebuildRouteProvider({
    lastUsedProvider,
    systemDefaultProvider,
  });
  const copy: IRebuildRouteStackedButtonCopy = {
    title: skipCachedPaths
      ? t("general.REBUILD_SAVE_CACHE_NO_CACHE")
      : t("general.REBUILD_ROUTE_DEFAULT"),
    hint: resolveProviderLabel(provider),
  };

  return copy;
};

export const formatRebuildRouteSaveCacheButtonTooltip = ({
  skipCachedPaths = false,
  t,
}: {
  skipCachedPaths?: boolean;
  t: TRebuildRouteTranslateFn;
}): string =>
  skipCachedPaths
    ? t("general.REBUILD_SAVE_CACHE_NO_CACHE_TOOLTIP")
    : t("general.REBUILD_SAVE_CACHE_TOOLTIP");

export const formatRebuildRouteFullRebuildButtonLabel = (
  params: IResolveInitialRebuildRouteProviderParams & {
    t: TRebuildRouteTranslateFn;
  },
): string => {
  const { title, hint } = formatRebuildRouteSaveCacheButtonCopy(params);
  return `${title} · ${hint}`;
};

/** Radio / button label for provider-owned station reorder. */
export const formatRebuildRouteProviderOptimizationLabel = ({
  provider,
  t,
}: IFormatRebuildRouteProviderLabelParams): string => {
  const providerLabel = resolveDirectionsServiceLabel(provider) ?? provider;
  return t("general.PROVIDER_OPTIMIZATION_NAMED", { provider: providerLabel });
};

export const formatRebuildRouteTypeLabel = ({
  provider,
  rebuildType,
  t,
}: IFormatRebuildRouteRerouteButtonLabelParams): string =>
  rebuildType === RebuildRouteTypeEnum.ProviderOptimization
    ? formatRebuildRouteProviderOptimizationLabel({ provider, t })
    : t(REBUILD_TYPE_TRANSLATION_KEY_MAP[rebuildType]);

/** Bottom submit: try another engine on this ride only, do not write cache. */
export const formatRebuildRouteNoCacheButtonCopy = ({
  provider,
  rebuildType,
  t,
}: IFormatRebuildRouteRerouteButtonLabelParams): IRebuildRouteStackedButtonCopy => {
  const providerLabel = resolveDirectionsServiceLabel(provider) ?? provider;
  const copy: IRebuildRouteStackedButtonCopy = {
    title: t("general.RE_ROUTE_THIS_RIDE_NO_CACHE"),
    hint: `${providerLabel} · ${formatRebuildRouteTypeLabel({
      provider,
      rebuildType,
      t,
    })}`,
  };

  return copy;
};

export const formatRebuildRouteRerouteButtonLabel = (
  params: IFormatRebuildRouteRerouteButtonLabelParams,
): string => {
  const { title, hint } = formatRebuildRouteNoCacheButtonCopy(params);
  return `${title} · ${hint}`;
};

export const formatAvoidTollsDefaultBadge = ({
  matchesDefault,
  defaultAvoidTolls,
  t,
}: {
  matchesDefault: boolean;
  defaultAvoidTolls: boolean;
  t: TRebuildRouteTranslateFn;
}): string =>
  matchesDefault
    ? t("general.REBUILD_ROUTE_PROVIDER_BADGE_DEFAULT")
    : t("general.AVOID_TOLLS_DEFAULT", {
        value: t(defaultAvoidTolls ? "general.YES" : "general.NO"),
      });

/** Cached / customized ML path id to open from the applied-route strip; hidden after live no-cache. */
export const resolveRebuildRouteAppliedMlPathId = ({
  usedCachedRouteId,
  customStationRouteId,
  routeRebuildSource,
  ignoreMLPathReason,
}: {
  usedCachedRouteId?: number | null;
  customStationRouteId?: number | null;
  routeRebuildSource?: RouteRebuildSourceEnum | null;
  ignoreMLPathReason?: IgnoreMLPathReasonEnum | null;
}): number | null => {
  if (ignoreMLPathReason) {
    return null;
  }

  if (routeRebuildSource === RouteRebuildSourceEnum.LiveWithoutCache) {
    return null;
  }

  return usedCachedRouteId ?? customStationRouteId ?? null;
};

/** Default-action rebuild types (Full rebuild plate), not custom reroutes like KeepOrder. */
export const isDefaultActionRebuildRouteType = (
  value: RebuildRouteTypeEnum | undefined,
): value is TDefaultActionRebuildRouteType =>
  value === RebuildRouteTypeEnum.Default ||
  value === RebuildRouteTypeEnum.RefreshCache ||
  value === RebuildRouteTypeEnum.SkipCacheWithoutUpdate;

/**
 * Maps the skip-cache checkbox to the default rebuild type.
 * When skip is on and cache must not be updated (ML path / course user-owned W), use SkipCacheWithoutUpdate.
 */
export const resolveSkipCacheDefaultRebuildType = ({
  skipCachedPaths,
  preferSkipCacheWithoutUpdate = false,
}: {
  skipCachedPaths: boolean;
  preferSkipCacheWithoutUpdate?: boolean;
}): RebuildRouteTypeEnum => {
  if (!skipCachedPaths) {
    return RebuildRouteTypeEnum.Default;
  }

  return preferSkipCacheWithoutUpdate
    ? RebuildRouteTypeEnum.SkipCacheWithoutUpdate
    : RebuildRouteTypeEnum.RefreshCache;
};

/** True when the applied rebuild should show the custom-type banner / * indicator. */
export const isCustomRebuildRouteType = (
  rebuildRouteType?: RebuildRouteTypeEnum | null,
): boolean =>
  rebuildRouteType !== undefined &&
  rebuildRouteType !== null &&
  rebuildRouteType !== RebuildRouteTypeEnum.Default;

/**
 * Full rebuild (Default / RefreshCache / SkipCacheWithoutUpdate) is the default
 * applied route in the UI — not a custom reroute like KeepOrder.
 */
export const toDisplayedRebuildRouteType = (
  rebuildType?: RebuildRouteTypeEnum | null,
): RebuildRouteTypeEnum => {
  if (
    !rebuildType ||
    rebuildType === RebuildRouteTypeEnum.RefreshCache ||
    rebuildType === RebuildRouteTypeEnum.SkipCacheWithoutUpdate
  ) {
    return RebuildRouteTypeEnum.Default;
  }

  return rebuildType;
};

/** Last applied engine or rebuild mode for the info strip (no button). */
export const formatRebuildRouteAppliedInfo = ({
  providerLabel,
  provider,
  appliedAtLabel,
  rebuildRouteType,
  routeRebuildSource,
  t,
}: IFormatRebuildRouteAppliedInfoParams): string => {
  const sourceLabel = formatRebuildRouteSourceLabel(routeRebuildSource, t);
  const displayedRebuildRouteType =
    toDisplayedRebuildRouteType(rebuildRouteType);
  const hasCustomRebuildType = isCustomRebuildRouteType(
    displayedRebuildRouteType,
  );

  if (hasCustomRebuildType) {
    const actionLabel = provider
      ? formatRebuildRouteTypeLabel({
          provider,
          rebuildType: displayedRebuildRouteType,
          t,
        })
      : t(REBUILD_TYPE_TRANSLATION_KEY_MAP[displayedRebuildRouteType]);

    if (sourceLabel) {
      if (appliedAtLabel) {
        return t("general.REBUILD_ROUTE_LAST_TYPE_APPLIED_WITH_TIME", {
          action: actionLabel,
          provider: providerLabel,
          source: sourceLabel,
          appliedAt: appliedAtLabel,
        });
      }

      return t("general.REBUILD_ROUTE_LAST_TYPE_APPLIED", {
        action: actionLabel,
        provider: providerLabel,
        source: sourceLabel,
      });
    }

    if (appliedAtLabel) {
      return t("general.REBUILD_ROUTE_LAST_TYPE_APPLIED_WITH_TIME_NO_SOURCE", {
        action: actionLabel,
        provider: providerLabel,
        appliedAt: appliedAtLabel,
      });
    }

    return t("general.REBUILD_ROUTE_LAST_TYPE_APPLIED_NO_SOURCE", {
      action: actionLabel,
      provider: providerLabel,
    });
  }

  if (sourceLabel) {
    if (appliedAtLabel) {
      return t("general.REBUILD_ROUTE_CURRENT_WITH_TIME", {
        provider: providerLabel,
        source: sourceLabel,
        appliedAt: appliedAtLabel,
      });
    }

    return t("general.REBUILD_ROUTE_CURRENT", {
      provider: providerLabel,
      source: sourceLabel,
    });
  }

  if (appliedAtLabel) {
    return t("general.REBUILD_ROUTE_CURRENT_WITH_TIME_NO_SOURCE", {
      provider: providerLabel,
      appliedAt: appliedAtLabel,
    });
  }

  return t("general.REBUILD_ROUTE_CURRENT_NO_SOURCE", {
    provider: providerLabel,
  });
};

/** Caption above the bottom submit: selected engine, mode, optional tolls. */
export const formatRebuildRouteRerouteSettingsSummary = ({
  provider,
  rebuildType,
  avoidTolls,
  t,
}: IFormatRebuildRouteRerouteSettingsParams): string => {
  const providerLabel = resolveDirectionsServiceLabel(provider) ?? provider;
  const parts = [
    providerLabel,
    formatRebuildRouteTypeLabel({ provider, rebuildType, t }),
  ];

  if (avoidTolls) {
    parts.push(t("general.AVOID_TOLLS"));
  }

  return parts.join(" · ");
};

export const createRouteRebuildAppliedAt = (): TDateISO =>
  new Date().toISOString();
